import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  selectConfigTilesetColorRampMax,
  selectConfigTilesetColorRampMin,
  selectConfigTilesetStyleColorConditions
} from '../../../features/config/config.selectors';
import {
  selectCurrentTilesetConfig
} from '../../../features/tileset-config/tileset-config.selectors';
import { TilesetConfig } from '../../../models/tileset-config';


@Component({
    selector: 'tileset-diff-color-ramp',
    templateUrl: './tileset-diff-color-ramp.component.html',
    styleUrls: ['./tileset-diff-color-ramp.component.scss'],
    standalone: false
})
export class TilesetDiffColorRampComponent implements AfterViewInit {

  @ViewChild('canvas') colorRampCanvas: ElementRef<HTMLCanvasElement> | undefined;
  @ViewChild('scale') valuesCanvas: ElementRef<HTMLCanvasElement> | undefined;
  private min: number = 0;
  private max: number = 1;
  private colors: Array<string> = [];


  constructor(private store: Store) {
    this.initHandleConfig();
    this.initHandleTilesetConfig();
  }

  private initHandleTilesetConfig(): void {
    this.store.select(selectCurrentTilesetConfig)
      .pipe(takeUntilDestroyed())
      .subscribe((tilesetConfig: TilesetConfig | null | undefined) => {
        this.min = 0;
        this.max = (tilesetConfig) ? tilesetConfig.maxDistance : 1;
      });
    this.store.select(selectConfigTilesetColorRampMin)
      .pipe(takeUntilDestroyed())
      .subscribe((value: number) => {
        this.min = value;
        this.drawValues(this.min, this.max);
      });
    this.store.select(selectConfigTilesetColorRampMax)
      .pipe(takeUntilDestroyed())
      .subscribe((value: number) => {
        this.max = value;
        this.drawValues(this.min, this.max);
      });
  }

  private initHandleConfig() {
    this.store.select(selectConfigTilesetStyleColorConditions)
      .pipe(takeUntilDestroyed())
      .subscribe(((conditions: Array<any> | undefined) => {
        const alteredConditions = (conditions) ? structuredClone(conditions) : [];
        this.colors = (conditions) ? alteredConditions?.map((c:any) => {
          const params = c[1];
          return "hsl("+ String(params[0]) + "turn,"+ String(params[1]*100) +"%, "+ String(params[2]*100) + "%)";
        }) : [];
        this.drawColorRamp(this.colors);
      }));
  }

  private generateGradient(colors: Array<string>): void {
    if(this.colorRampCanvas) {
      const ctx = this.colorRampCanvas.nativeElement.getContext('2d');
      if (ctx && colors.length) {
        const width = this.colorRampCanvas.nativeElement.width;
        const height = this.colorRampCanvas.nativeElement.height;
        const grd = ctx.createLinearGradient(0, 0, width, 0);
        colors.forEach((color: string, index: number) => {
          grd.addColorStop(index / (colors.length - 1), color);
        });
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, width, height);
      }
    }
  }

  private generateValues(min: number, max: number, nbStep: number): void {
    if (!this.valuesCanvas) return;

    const canvas = this.valuesCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cssWidth = 220;
    const cssHeight = 25;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;

    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const step = cssWidth / nbStep;

    ctx.font = '12px Inter';
    ctx.textBaseline = 'top';

    for (let i = 0; i <= nbStep; i++) {
      let lineX = i * step;

      if (i === 0) lineX += 1;
      if (i === nbStep) lineX -= 1;

      ctx.beginPath();
      ctx.moveTo(lineX, 0);
      ctx.lineTo(lineX, 5);
      ctx.stroke();

      const value = min + (i / nbStep) * (max - min);
      const text = value.toFixed(2);
      const metrics = ctx.measureText(text);

      let textX = lineX;

      if (i === 0) {
        textX = 0;
      } else if (i === nbStep) {
        textX = cssWidth - metrics.width;
      } else {
        textX = lineX - metrics.width / 2;
      }

      ctx.fillText(text, textX, 10);
    }
  }

  private clearColorRamp(): void {
    if(this.colorRampCanvas) {
      const ctx = this.colorRampCanvas.nativeElement.getContext('2d')
      if (ctx) {
        const width = this.colorRampCanvas.nativeElement.width;
        const height = this.colorRampCanvas.nativeElement.height;
        ctx.clearRect(0, 0, width, height);
      }
    }
  }

  private clearValues(): void {
    if(this.valuesCanvas) {
      const ctx = this.valuesCanvas.nativeElement.getContext('2d')
      if (ctx) {
        const width = this.valuesCanvas.nativeElement.width;
        const height = this.valuesCanvas.nativeElement.height;
        ctx.clearRect(0, 0, width, height);
      }
    }
  }

  private drawColorRamp(colors: any): void {
    this.clearColorRamp();
    this.generateGradient(colors);
  }

  private drawValues(min: number, max: number): void {
    this.clearValues();
    this.generateValues(min, max, 4);
  }

  ngAfterViewInit(): void {
    this.drawColorRamp(this.colors);
    this.drawValues(this.min, this.max);
  }
}
